import React, { useEffect, useState } from "react";
import { Button, Typography, Box, TextField, useTheme, Link as ExtLink } from "@mui/material";
import CopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import { fetchImage, getImagePreview, getMap, loadImageFromCache, useCheckout, useGeneratedImage, useImageAPIError, useImagePreview, useIsLoading } from "../../state/image";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Title from "../title";
import { GradientText } from "../dashboard/Gradient";
import HighQualityIcon from '@mui/icons-material/HighQuality';
import RedditIcon from '@mui/icons-material/Reddit';
import EmailIcon from '@mui/icons-material/Email';
import TwitterIcon from '@mui/icons-material/Twitter';
import PinterestIcon from '@mui/icons-material/Pinterest';
import HomeIcon from '@mui/icons-material/Home';
import { STATIC_FILE_URL } from "../../constants";
import { Elevation } from "../dashboard/Elevation";
import posthog from "posthog-js";
import { colors } from "../../colors2";
import { useAppDispatch } from "@/state/types";

const ViewMap = () => {
  const { token } = useParams<{ token: string | undefined }>();
  const { palette, breakpoints } = useTheme();
  const dispatch = useAppDispatch();
  const image = useGeneratedImage();
  const error = useImageAPIError();

  useEffect(() => {
    if (token) {
      dispatch(getMap(token))
    }
    // dispatch(loadImageFromCache());
  }, [dispatch])

  if (!token || typeof token !== "string") {
    return null;
  }
  // TODO: Change header to: "This map was created with Mappin"
  // TODO: Add disclaimer that images only present for 90 days
  // Also add share buttons to create your own map
  // Add a "see map" page where it just has a thumbnail 
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h2" mb="1rem" textAlign="center">
        This map was created with <GradientText variant="h2" display="inline">Mappin
          <img
            src={`${STATIC_FILE_URL}/pin-icon.png`}
            width="16px"
            height="16px"
            style={{ alignSelf: "end" }}
            alt="mappin-pin"
          />
      </GradientText>
      </Typography>
      <Box mb="1rem">
        {image && (
          <img alt="created-map" src={image} style={{ maxWidth: "60vw", maxHeight: "60vh" }}/>
        )}
      </Box>
      <Box>
        <Button
          startIcon={<HighQualityIcon/>}
          component="a"
          href={image}
          variant="outlined"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none", mb: "16px" }}
        >
          View High-Resolution Map
        </Button>
        <Button
          startIcon={<HomeIcon/>}
          component={Link}
          to="/"
          variant="contained"
          sx={{ textTransform: "none", mb: "16px", ml: "16px" }}
        >
          Go to homepage
        </Button>
      </Box>
      {/* <Typography variant="body1" style={{margin: "16px 0px"}} textAlign="center">
        To obtain your image, just click "View High-Resolution Map", right click the image and click "Save as".
      </Typography> */}
      <Elevation
        p="1rem"
        alignItems="flex-start"
        display="flex"
        flexDirection="column"
        my={2}
        enableMaxWidth
        zIndex={1}
        width="90%"
        boxShadow={`${palette.secondary.light} 2px 2px 12px`}
      >
        <Typography variant="h3" textAlign="left" mb="1rem">
          Share your map
        </Typography>
        <Typography variant="body1" textAlign="left">
        Send your custom map to friends and let them design their own with Mappin — share this link to give them access.
        </Typography>
        <ShareLinkBox baseURL={window.location.toString()} imgURL={image} />
    </Elevation>
    <Typography variant="body1" color="primary" sx={{ fontSize: "10px" }} alignSelf="flex-start" ml="32px">
        Maps link valid for 60 days.
      </Typography>
      <Typography variant="body1" color="primary" sx={{ my: "8px" }} textAlign="center">
        If you have any problems getting your image preview, please contact {" "}
        <a style={{color: "white" }} href={"mailto:help@mappin.pro"}>help@mappin.pro</a>.
      </Typography>
    </Box>
  );
};

const ShareLinkBox = ({ baseURL, imgURL }:{ baseURL: string; imgURL?: string }) => {
  const [isCopied, setIsCopied] = useState(false);
  const copyLink = `${baseURL}?utm_source=link&utm_campaign=v1&utm_medium=share`;
  const emailShareLink = `${baseURL}?utm_source=email&utm_campaign=v1&utm_medium=share`;
  const emailLink = `mailto:?subject=I made a map with Mappin!&body=Hi, look at this map I made using Mappin: ${encodeURIComponent(emailShareLink)}`

  const xShareLink = `${baseURL}?utm_source=x&utm_campaign=v1&utm_medium=share`;
  const xLink = `https://x.com/intent/tweet/?text=${encodeURIComponent("Have a look at this map I created using Mappin")}&url=${encodeURIComponent(xShareLink)}&via=MappinCreations`
  
  const redditShareLink = `${baseURL}?utm_source=reddit&utm_campaign=v1&utm_medium=share`;
  const redditLink = `https://www.reddit.com/submit?url=${encodeURIComponent(redditShareLink)}&title=${encodeURIComponent("I generated a custom map on Mappin. What do you think?")}`
  
  const pinterestShareLink = `${baseURL}?utm_source=pinterest&utm_campaign=v1&utm_medium=share`;
  const pinterestLink = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pinterestShareLink)}&media=${imgURL}&description=My custom map design - made with Mappin.`
  
  const captureAnalytics = (name: string) => () => posthog.capture("share_link_clicked", { via: name })
  useEffect(() => {
    if (isCopied) {
      setTimeout(() => setIsCopied(false), 2000)
    }
  }, [isCopied, setIsCopied])
  return (
    <Box color={colors.text.brand.darkest} width="100%">
      <TextField
        disabled 
        fullWidth
        sx={{ fontSize: "12px", mt: "1rem", p: 0, maxWidth: "400px" }}
        value={copyLink}
        InputProps={{
          sx: { p: 0, pl: "4px" },
          inputProps: {
            style: { padding: 0, fontSize: "12px" },
          },
          endAdornment: (
            <Button
              startIcon={isCopied ? <DoneIcon/> : <CopyIcon/>}
              sx={{ p: "4px 1rem", textTransform: "none", color: "white" }}
              onClick={async () => { await navigator.clipboard.writeText(copyLink); setIsCopied(true); captureAnalytics("copy")(); }}
            >
              {isCopied ? "Copied" : "Copy"}
            </Button>
          ),
        }}
      />
      <Box display="flex" justifyContent="flex-start" gap="1.5rem" mt="1rem">
        <ExtLink href={emailLink} target="_blank" onClick={captureAnalytics("email")}>
          <EmailIcon sx={{ color: "white" }} />
        </ExtLink>
        <ExtLink href={xLink} target="_blank" onClick={captureAnalytics("x")}>
          <TwitterIcon sx={{ color: "white" }} />
        </ExtLink>
        <ExtLink href={redditLink} target="_blank" onClick={captureAnalytics("reddit")}>
          <RedditIcon sx={{ color: "white" }} />
        </ExtLink>
        <ExtLink href={pinterestLink} target="_blank" onClick={captureAnalytics("pinterest")}>
          <PinterestIcon sx={{ color: "white" }} />
        </ExtLink>
          {/* Pari waz here */}
        </Box>
      </Box>
  )
}

export default ViewMap;
